import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { activeOnly } from "@/lib/users.js";
import { Field, Input, Select, Textarea } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import { idOf } from "@/lib/teams.js";

/*
 * Create / edit a team. Fields are the ones createTeam and updateTeam accept:
 *
 *   create  POST  /api/teams       { name, description, members }
 *   edit    PATCH /api/teams/:id   { name, description, manager, members }
 *
 * Members are NOT here — they get their own drawer, because picking a dozen
 * people is a different task from naming a team, and mixing them means every
 * rename re-sends the whole member list.
 *
 * Manager is edit-only: createTeam ignores the field and assigns the creator.
 */
const schema = z.object({
    name: z.string().trim().min(1, "Please enter a team name"),
    description: z.string().trim().default(""),
    manager: z.string().default(""),
});

export default function TeamFormDrawer({ open, team, users = [], onClose, onSubmit, saving, error }) {
    const editing = Boolean(team?._id);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ resolver: zodResolver(schema), mode: "onSubmit", reValidateMode: "onChange" });

    useEffect(() => {
        if (!open) return;
        reset({
            name: team?.name ?? "",
            description: team?.description ?? "",
            manager: idOf(team?.manager) ?? "",
        });
    }, [open, team, reset]);

    // Only members can manage the team, so offering anyone else would create a
    // manager the backend's own "managers may only touch their teams" rule then
    // locks out of it.
    const candidates = editing
        ? activeOnly(users).filter(
              (u) =>
                  (team.members ?? []).some((m) => idOf(m) === u._id) ||
                  u.role === "admin" ||
                  u.role === "manager",
          )
        : [];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={editing ? "Edit team" : "New team"}
            subtitle={editing ? team.name : "Group people so reports and schedules can target them"}
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="team-form"
                        loading={saving}
                        data-save-team
                    >
                        {editing ? "Save changes" : "Create team"}
                    </Button>
                </>
            }
        >
            <form id="team-form" onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                {error && <Callout tone="danger">{error}</Callout>}

                <Field label="Team name" htmlFor="t-name" error={errors.name?.message}>
                    <Input id="t-name" data-autofocus autoComplete="off" {...register("name")} />
                </Field>

                <Field
                    label="Description"
                    htmlFor="t-description"
                    optional
                    error={errors.description?.message}
                    hint="What this team is responsible for. Shown on the team card."
                >
                    <Textarea id="t-description" rows={3} {...register("description")} />
                </Field>

                {editing ? (
                    <Field
                        label="Manager"
                        htmlFor="t-manager"
                        hint="Managers can edit their own team and see its reports. A team always has one."
                    >
                        <Select id="t-manager" {...register("manager")}>
                            {/* No "None" option: Team.manager is required in the
                                schema and updateTeam ignores a falsy value, so
                                clearing is not something the API can be asked
                                for. A team only ends up manager-less when the
                                manager's ACCOUNT is deleted, which nulls the
                                reference directly. */}
                            <option value="" disabled>
                                Select a manager…
                            </option>
                            {candidates.map((u) => (
                                <option key={u._id} value={u._id}>
                                    {u.name} · {u.role}
                                </option>
                            ))}
                        </Select>
                    </Field>
                ) : (
                    <Callout tone="info">
                        You become this team's manager. Change it from{" "}
                        <strong className="font-semibold">Edit team</strong> once people are in, and
                        add members with <strong className="font-semibold">Manage</strong>.
                    </Callout>
                )}
            </form>
        </Drawer>
    );
}
